/**
 * 新規 device 登録の本人確認 gate (#791 ②).
 *
 * #792 監査の「未確認の新規 passkey 登録」が存在する場合、次回ログイン時に
 * **アプリの全 page 利用を block** し DeviceConfirmModal を強制表示する。
 * Google 乗っ取り後に attacker が silent 登録した passkey を本人に気付かせ、
 * 「心当たり無し」なら全 credential 失効させる検知/対応経路。
 *
 * 戻り値:
 *   - `requiresConfirmation: true`: 未確認の新規 device あり → blocker UI
 *   - `pending`: 確認対象の device 一覧
 *   - `loading: true`: fetch 中
 *
 * 設計: useEnrollmentGate と同じく useSyncExternalStore で session-wide cache を
 * 共有。acknowledge / revoke 後は `markDeviceConfirmed()` で再評価。
 *
 * ⚠️ 本 gate 単体の限界 (= #791 ② は「検知/対応」層であり予防層ではない):
 *   - 「次にログインした人が確認する」モデルのため、攻撃者が legit user より
 *     先に頻繁にログインすると攻撃者が自分の不正 credential を自分で
 *     acknowledge できる。ATO 完全防御を本 gate 単体に依存しないこと。
 *     予防は #793 ④ (異常 enrollment の risk-based step-up)、決済保護は
 *     #790 (forced 3DS) + #792 (監査) の多層で補完される前提。
 *   - fetch 失敗時は fail-open (= login flow を brick しない)。security gate
 *     としては fail-open だが、#790 3DS + #792 監査が backstop のため許容。
 */
import { useEffect, useSyncExternalStore } from 'react'
import { useStore } from '../store/useStore'
import { api, type PendingDeviceConfirmation } from '../services/api'

type GateState = {
  pending: PendingDeviceConfirmation[] | null  // null = not evaluated yet
  loading: boolean
}

let state: GateState = { pending: null, loading: false }
const listeners = new Set<() => void>()

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function getSnapshot(): GateState {
  return state
}

function setState(next: Partial<GateState>): void {
  if (
    (next.pending === undefined || next.pending === state.pending) &&
    (next.loading === undefined || next.loading === state.loading)
  ) {
    return
  }
  // useSyncExternalStore は getSnapshot の戻り値を Object.is で比較し、同一
  // 参照だと state が変わっても再描画しない。可変シングルトンを in-place 変更
  // すると getSnapshot が常に同じ ref を返し、acknowledge 後の markDeviceConfirmed
  // 通知で modal が閉じない (リロードで再マウントするまで残る) 不具合になる。
  // 変更時は必ず新しい object を割り当てて ref を変える。
  state = {
    pending: next.pending !== undefined ? next.pending : state.pending,
    loading: next.loading !== undefined ? next.loading : state.loading,
  }
  for (const l of listeners) l()
}

let fetchInFlight = false

async function evaluate(): Promise<void> {
  if (fetchInFlight) return
  fetchInFlight = true
  setState({ loading: true })
  try {
    const resp = await api.getPendingDeviceConfirmation().catch(() => ({ pending: [] }))
    setState({ pending: resp?.pending || [], loading: false })
  } catch {
    // fail-safe: gate を通す (= login flow を詰まらせない)
    setState({ pending: [], loading: false })
  } finally {
    fetchInFlight = false
  }
}

/**
 * acknowledge / revoke 完了後に呼ぶ。表示中の pending は全て確定済なので
 * **楽観的に `pending: []` を直接セット**する (modal 即 unmount)。
 *
 * `pending: null` にしてはいけない: useEffect が null を「未評価」とみなし
 * 即 evaluate() で再 fetch するが、それが直前に書いた
 * confirmedAt (SERVER_TIMESTAMP) と Firestore クエリ整合性で競合し、
 * まだ未確認に見える doc を取得 → modal が一瞬閉じて再表示される
 * (リロードするまで残る) #791② のバグになる。
 * useEnrollmentGate.markEnrolled が requires:false を直接セットするのと
 * 同じ楽観方針。fresh 再評価は次ログイン時 (logout で
 * resetDeviceConfirmGate → null → evaluate) に行われる。
 */
export function markDeviceConfirmed(): void {
  setState({ pending: [], loading: false })
}

/** Logout 時に呼ぶ — cache クリア (別ユーザー再ログインで再 fetch). */
export function resetDeviceConfirmGate(): void {
  setState({ pending: null, loading: false })
}

export function useDeviceConfirmGate(): {
  requiresConfirmation: boolean
  pending: PendingDeviceConfirmation[]
  loading: boolean
} {
  const isLoggedIn = useStore((s) => s.isLoggedIn)
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)

  useEffect(() => {
    if (!isLoggedIn) {
      if (snapshot.pending !== null) {
        setState({ pending: null })
      }
      return
    }
    if (snapshot.pending === null && !snapshot.loading) {
      void evaluate()
    }
  }, [isLoggedIn, snapshot.pending, snapshot.loading])

  return {
    requiresConfirmation: (snapshot.pending?.length ?? 0) > 0,
    pending: snapshot.pending || [],
    loading: snapshot.loading,
  }
}
